import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import { useForm } from "@mantine/form";
import { AuthFormWrapper } from "../AuthFormWrapper";
import { addToast, Button } from "@heroui/react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { TurnstileCompat } from "@/Components/TurnstileCompat";
import { markSignupCompleted } from "@/utils/signupTracking";
import { parseAsString, parseAsStringLiteral, useQueryState } from "nuqs";
import { rememberAppliedPromoCode } from "@/features/self-onboarding/appliedPromoCode";
import { OnboardingPromoCode } from "@/features/self-onboarding/OnboardingPromoCode";
import { CustomerAppDownload } from "./CustomerAppDownload";
import { InAppBrowserNotice } from "./InAppBrowserNotice";
import { RegisterRoleChoice } from "./RegisterRoleChoice";
import {
  detectDevicePlatform,
  getDirectStoreUrl,
  pickLinkCode,
  REGISTER_ROLES,
  type RegisterRole,
  resolveRegisterRole,
} from "./registerRole";

// The check is rate limited per address, so it waits for a pause in typing and
// for enough characters to be a plausible code.
const PROMO_CODE_DEBOUNCE_MS = 700;
const MIN_PROMO_CODE_LENGTH = 4;

export const Register = () => {
  const {
    mutateAsync: register,
    isPending,
    processedErrorMessage,
  } = api.auth.useRegister();
  const { preferredLanguage } = usePreferredLanguageStore((state) => state);
  const [roleFromLink, setRole] = useQueryState(
    "role",
    parseAsStringLiteral(REGISTER_ROLES).withOptions({ history: "push" }),
  );

  // A referral link carries the code: /register?referralCode=ANA50
  const [referralCodeFromLink] = useQueryState("referralCode", parseAsString);
  const [legacyCodeFromLink] = useQueryState("code", parseAsString);
  const codeFromLink = pickLinkCode(referralCodeFromLink, legacyCodeFromLink);
  const role = resolveRegisterRole(roleFromLink, codeFromLink);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const isTurnstileDisabled = import.meta.env.VITE_DISABLE_TURNSTILE === "true";

  const form = useForm({
    initialValues: {
      email: "",
      language: "sl",
      promoCode: codeFromLink ?? "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value.trim()) ? null : t`Neveljaven e-poštni naslov`,
    },
  });

  const {
    mutateAsync: checkPromoCode,
    isPending: isCheckingPromoCode,
    data: promoCodeCheck,
    reset: resetPromoCodeCheck,
  } = api.promoCodes.useCheckPromoCode();

  const [debouncedPromoCode] = useDebouncedValue(
    form.values.promoCode.trim(),
    PROMO_CODE_DEBOUNCE_MS,
  );

  useEffect(() => {
    if (debouncedPromoCode.length < MIN_PROMO_CODE_LENGTH) {
      resetPromoCodeCheck();
      return;
    }

    // A failed check is not fatal: sign-up validates the code again.
    checkPromoCode({ code: debouncedPromoCode }).catch(() => undefined);
  }, [debouncedPromoCode, checkPromoCode, resetPromoCodeCheck]);

  const onSubmitForm = async (values: typeof form.values) => {
    if (!turnstileToken && !isTurnstileDisabled) {
      addToast({
        title: t`Napaka`,
        description: t`Verifikacija z Captcha ni uspela. Osvežite stran in poskusite znova.`,
        color: "warning",
      });
      setTurnstileToken(null);
      setTurnstileKey((prev) => prev + 1);
      return;
    }

    const registerData = {
      email: values.email.trim(),
      language: preferredLanguage.userPreferredLanguage,
      turnstileToken: turnstileToken || "",
      promoCode: values.promoCode.trim() || undefined,
    };

    try {
      const response = await register(registerData);

      markSignupCompleted(preferredLanguage.userPreferredLanguage);

      rememberAppliedPromoCode(response.promoCode);

      localStorage.setItem("usr_token", response.token);
      // navigate(0);
      window.location.reload();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        color: "danger",
      });
    }

    if (!isTurnstileDisabled) {
      setTurnstileToken(null);
      setTurnstileKey((prev) => prev + 1);
    }
  };

  const pageTitle = t`Registracija`;

  function handleRoleSelect(selected: RegisterRole) {
    void setRole(selected);

    if (selected !== "customer") return;

    const storeUrl = getDirectStoreUrl(detectDevicePlatform());

    if (storeUrl) window.location.assign(storeUrl);
  }

  // Skipped by `/register?role=business` or by a link that carries a code.
  if (role !== "business") {
    return (
      <>
        <title>{pageTitle}</title>
        <AuthFormWrapper isLogin={false}>
          {role === "customer" ? (
            <CustomerAppDownload onBack={() => void setRole(null)} />
          ) : (
            <RegisterRoleChoice onSelect={handleRoleSelect} />
          )}
        </AuthFormWrapper>
      </>
    );
  }

  return (
    <>
      <title>{pageTitle}</title>
      <AuthFormWrapper isLogin={false}>
        <form
          style={{ display: "contents" }}
          onSubmit={form.onSubmit(onSubmitForm)}
          noValidate
        >
          <InAppBrowserNotice />

          <LimeInput
            label={t`E-poštni naslov`}
            data-identifier={"register-email-input"}
            size="sm"
            className="w-full"
            {...form.getInputProps("email")}
            autoComplete="email"
            name="email"
            error={form.errors.email}
          />

          <OnboardingPromoCode
            inputProps={form.getInputProps("promoCode")}
            isChecking={isCheckingPromoCode}
            result={promoCodeCheck}
          />

          {!isTurnstileDisabled && (
            <TurnstileCompat
              key={turnstileKey}
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={(error) => {
                console.error(error);
                setTurnstileToken(null);
              }}
            />
          )}

          <LimeAlert message={processedErrorMessage} />

          <Button
            className="mt-2 w-full"
            type="submit"
            isLoading={isPending}
            data-identifier="continue-button"
            color="primary"
          >
            <Trans>Nadaljuj</Trans>
          </Button>
        </form>
      </AuthFormWrapper>
    </>
  );
};
